import { QueryParamsHandling } from '@angular/router';

import { MenuItem, TooltipOptions } from 'primeng/api';

import { ICellRendererParams, RowNode } from 'ag-grid-enterprise';

import { ISummitGroupEvent } from './i-summit-group-event';
import { SummitIdObject } from './summit-id-object';

export class SummitGroupMenuItem<T extends SummitIdObject> implements MenuItem {
  //adding callbacks
  disabledCallback?: (elements: T[]) => boolean;
  visibleCallback?: (elements: T[]) => boolean;
  itemsCallback?: (elements: T[]) => MenuItem[];

  //MenuItem properties
  label?: string;
  icon?: string;
  command?: (event: ISummitGroupEvent<T>) => void;
  url?: string;
  items?: SummitGroupMenuItem<T>[];
  expanded?: boolean;
  disabled?: boolean;
  visible?: boolean;
  target?: string;
  escape?: boolean;
  routerLinkActiveOptions?: any;
  separator?: boolean;
  badge?: string;
  tooltip?: string;
  tooltipPosition?: string;
  badgeStyleClass?: string;
  style?: any;
  styleClass?: string;
  title?: string;
  id?: string;
  automationId?: any;
  tabindex?: string;
  routerLink?: any;
  queryParams?: {
    [k: string]: any;
  };
  fragment?: string;
  queryParamsHandling?: QueryParamsHandling;
  preserveFragment?: boolean;
  skipLocationChange?: boolean;
  replaceUrl?: boolean;
  iconStyle?: any;
  iconClass?: string;
  state?: {
    [k: string]: any;
  };
  tooltipOptions?: TooltipOptions;

  //Static Functions
  static applyCallBacks<T extends SummitIdObject>(item: SummitGroupMenuItem<T>, elements: T[], disableAll: boolean) {
    if (disableAll) {
      item.disabled = true;
    } else if (item.disabled === undefined && item.disabledCallback) item.disabled = item.disabledCallback(elements);

    if (item.visible === undefined && item.visibleCallback) item.visible = item.visibleCallback(elements);
    if (item.items === undefined && item.itemsCallback)
      item.items = item.itemsCallback(elements) as SummitGroupMenuItem<T>[];

    return item;
  }

  static setElements<T extends SummitIdObject>(
    items: SummitGroupMenuItem<T>[] | undefined,
    elements: T[] | undefined,
    params: ICellRendererParams | undefined,
    selectAllMenuOptions?: MenuItem[] | null,
    disableAll?: boolean
  ): SummitGroupMenuItem<T>[] {
    //console.log('setElements', disableAll);
    if (!items || !elements) return [];
    const lclMulti = items.map((item) => Object.assign({}, item));
    lclMulti.map((item) => {
      const oldCommand = item.command;
      item.command = ($event?: ISummitGroupEvent<T>) => {
        if ($event) {
          $event.elementIds = elements.filter((row) => row.id !== null).map((row) => row.id!.toString());
          $event.elements = elements;
          if (params) {
            //Group Menu Object
            //params are for groups, params are null for selected rows
            $event.groupKeys = SummitGroupMenuItem.BuildGroupFields<T>(params.node as RowNode<T>);
            $event.firstChild = $event.elements[0];
            $event.lastChild = $event.elements[$event.elements.length - 1];
            $event.defaultObject = SummitGroupMenuItem.BuildGroupObject($event.groupKeys, $event.firstChild);
          } else {
            //console.log('Building Multi-Select = ', items, elements);
            $event.firstChild = elements ? elements[0] : undefined;
            $event.lastChild = elements ? elements[elements.length - 1] : undefined;
          }
          //$event.element = elements[0];
          if (oldCommand) oldCommand($event);
        }
      };
      SummitGroupMenuItem.applyCallBacks(item, elements, disableAll ?? false);
      if (item.items) item.items = SummitGroupMenuItem.setElements(item.items, elements, params, null, disableAll);
    });
    if (selectAllMenuOptions) return [...(selectAllMenuOptions as SummitGroupMenuItem<T>[]), ...lclMulti];
    return lclMulti;
  }

  static BuildGroupFields<T>(node: RowNode<T>): Array<keyof T> {
    if (!node.parent || !node.group || node.level <= -1) return [];
    const groupObject = SummitGroupMenuItem.BuildGroupFields(node.parent);
    if (node.field) groupObject.push(node.field as keyof T);
    return groupObject;
  }

  static BuildGroupObject<T>(keys: Array<keyof T>, data: T): Partial<T> {
    const groupObject = {} as Partial<T>;
    if (data) {
      keys.forEach((key) => {
        groupObject[key] = data[key];
      });
    }
    return groupObject;
  }
}
